import Promise from 'bluebird';
import _ from 'lodash';
import { computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';

import {
  Button,
  Col,
  Dropdown,
  Empty,
  Menu,
  message,
  Modal,
  Row,
  Spin,
  Switch,
} from 'antd';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import {
  customFleets,
  download,
  reportConfig,
  webServiceProvider,
} from 'shared';

import {
  DownloadOutlined,
  DownOutlined,
  LoadingOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import { ShopStore } from 'stores';
import AppStore from 'stores/App';
import TripDetailArea from './TripDetailArea';

const { confirm } = Modal;

const TripMenubarText = styled.span`
  font-size: 14px;
  margin: auto 10px;
`;

const TripMenubarButton = styled(Button)`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

class Utilities extends Component {
  static propTypes = {
    history: PropTypes.object,
    trips: PropTypes.object,
    selectedTrip: PropTypes.object,
    car: PropTypes.object,
    page: PropTypes.number,
    pageSize: PropTypes.number,
    openSimilarTrips: PropTypes.func,
    openCompareTrips: PropTypes.func,
    getTrips: PropTypes.func,
    setIsAllTripsOnMap: PropTypes.func,
  };

  observableCar = null;

  state = {
    isSnapToRoadToggled: false,
    isPIDToggled: false,
    isEmissionToggled: false,
    isDownloadTripAttachments: false,
    isDownloadSensorDataCsvWithPitstopMeta: false,
    isDownloadSensorDataCsv: false,
    isDownloadAllSensorDataCsv: false,
    isDownloadTripsCsv: false,
    isDownloadPidData: false,
    isAllTripsOnMap: false,
    similarTrips: false,
    compareTrips: false,
  };

  constructor(props) {
    super(props);

    this.observableCar = observable.box(props.car || null);

    makeObservable(this, {
      shopId: computed,
      isPIDToggled: computed,
      observableCar: observable,
    });
  }

  componentDidMount() {
    if (this.props.car) {
      this.observableCar.set(this.props.car);

      this.setState({
        isPIDToggled: customFleets.npl.includes(this.shopId),
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.car !== this.props.car && this.props.car) {
      this.observableCar.set(this.props.car);
    }
  }

  get history() {
    return this.props.history;
  }

  get isPIDToggled() {
    return customFleets.npl.includes(this.shopId);
  }

  get shopId() {
    const car = this.observableCar.get();
    return car
      ? car.shopId || ShopStore.currentShop.id
      : ShopStore.currentShop.id;
  }

  onDownloadTripAttachments = async () => {
    let { selectedTrip } = this.props;

    try {
      this.setState({
        isDownloadTripAttachments: true,
      });

      await selectedTrip.getAttachments();

      if (!selectedTrip.isError) {
        let { attachments } = selectedTrip;

        if (_.isEmpty(attachments)) {
          AppStore.addNotification('There are no files to download!');
        } else {
          await Promise.map(
            attachments,
            async (file) => {
              let _file = file['Key'].split('/');
              let fileName = _file[_file.length - 1];

              let response = await webServiceProvider.fetchCSV(
                'v1/attachments',
                {
                  filename: file['Key'],
                }
              );
              download(response, fileName);
            },
            { concurrency: 3 }
          );

          AppStore.addSuccess('Your files are downloaded successfully!');
        }
      }
    } catch (err) {
      AppStore.addError('Sorry! We cannot download your files at the moment!');
    } finally {
      this.setState({
        isDownloadTripAttachments: false,
      });
    }
  };

  onDownloadSensorDataCSV = async (options) => {
    let { car, selectedTrip } = this.props;

    try {
      if (options.includePitstopMetaData) {
        this.setState({
          isDownloadSensorDataCsvWithPitstopMeta: true,
        });
      } else {
        this.setState({
          isDownloadSensorDataCsv: true,
        });
      }

      await car.downloadSensorDataCSV({ trip: selectedTrip, ...options });
    } catch (err) {
      AppStore.addError(err.message);
    } finally {
      if (options.includePitstopMetaData) {
        this.setState({
          isDownloadSensorDataCsvWithPitstopMeta: false,
        });
      } else {
        this.setState({
          isDownloadSensorDataCsv: false,
        });
      }
    }
  };

  onDownloadAllSensorDataCSV = async (options) => {
    let { car } = this.props;

    try {
      this.setState({
        isDownloadAllSensorDataCsv: true,
      });

      await Promise.map(
        car.trips.data,
        async (trip) => {
          await car.downloadSensorDataCSV({ trip, ...options });
        },
        { concurrency: 3 }
      );
    } catch (err) {
      AppStore.addError(err.message);
    } finally {
      this.setState({
        isDownloadAllSensorDataCsv: false,
      });
    }
  };

  onDownloadTripsCsv = async (options) => {
    try {
      this.setState({
        isDownloadTripsCsv: true,
      });

      await this.props.car.downloadTripsCSV(options);
    } catch (err) {
      AppStore.addError(err.message);
    } finally {
      this.setState({
        isDownloadTripsCsv: false,
      });
    }
  };

  onDownloadGMRVPidData = async () => {
    try {
      this.setState({
        isDownloadPidData: true,
      });

      AppStore.addLoading('Getting sensor data...');

      await this.props.car.downloadGMRVPidData({
        trip: this.props.selectedTrip,
        order: 'ASC',
      });

      AppStore.addSuccess('Getting PID data is done!');
    } catch (err) {
      AppStore.addError(err.message);
    } finally {
      this.setState({
        isDownloadPidData: false,
      });
      AppStore.removeLoading('Getting sensor data...');
    }
  };

  showDeleteConfirm(deleteFunction) {
    confirm({
      title: 'Are you sure that you want to delete this trip?',
      content: 'Deleting a trip cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        deleteFunction();
      },
      onCancel() {
        // console.log('Cancel');
      },
    });
  }

  deleteTrip = async () => {
    try {
      await webServiceProvider.delete(
        `v1/trip/${this.props.selectedTrip.tripId}`
      );
      message.success('Trip successfully deleted!');

      if (this.history.location.pathname.includes('/sharetrip')) {
        this.history.push('/report');
      }

      if (this.props.getTrips) await this.props.getTrips();
    } catch (e) {
      AppStore.addError(e);
    }
  };

  menu = () => {
    let {
      isDownloadTripAttachments,
      isDownloadSensorDataCsvWithPitstopMeta,
      isDownloadSensorDataCsv,
      isDownloadAllSensorDataCsv,
      isDownloadTripsCsv,
      isDownloadPidData,
    } = this.state;

    let { page, pageSize } = this.props;

    let menuItems = [
      {
        key: '1',
        onClick: () => this.onDownloadTripAttachments(),
        label: !isDownloadTripAttachments ? (
          <>Download all attachments from the current Trip</>
        ) : (
          <LoadingOutlined />
        ),
      },
      {
        key: '2',
        onClick: () =>
          this.onDownloadSensorDataCSV({
            includePitstopMetaData: true,
          }),
        label: !isDownloadSensorDataCsvWithPitstopMeta ? (
          <>Download Sensor Data for current trip</>
        ) : (
          <LoadingOutlined />
        ),
      },
      {
        key: '3',
        onClick: () =>
          this.onDownloadSensorDataCSV({
            includePitstopMetaData: false,
          }),
        label: !isDownloadSensorDataCsv ? (
          <>Download Sensor Data for current trip (without Pitstop metadata)</>
        ) : (
          <LoadingOutlined />
        ),
      },
      {
        key: '4',
        disabled: this.history.location.pathname.includes('/sharetrip'),
        onClick: () =>
          this.onDownloadAllSensorDataCSV({
            includePitstopMetaData: true,
          }),
        label: !isDownloadAllSensorDataCsv ? (
          <>Download Sensor Data for all trips on page</>
        ) : (
          <LoadingOutlined />
        ),
      },
      {
        key: '5',
        disabled: this.history.location.pathname.includes('/sharetrip'),
        onClick: () =>
          this.onDownloadTripsCsv({
            includePitstopMetaData: true,
            offset: (page - 1) * pageSize,
            pageSize: pageSize,
          }),
        label: !isDownloadTripsCsv ? (
          <>Download Trips as CSV</>
        ) : (
          <LoadingOutlined />
        ),
      },
    ];

    if (customFleets.gmrv.includes(this.shopId)) {
      menuItems = _.filter(menuItems, (item) => ['1'].includes(item.key));
      menuItems.push({
        key: '6',
        onClick: () => this.onDownloadGMRVPidData(),
        label: !isDownloadPidData ? (
          <>Download Sensor Data (Only Numbers) for current trip</>
        ) : (
          <LoadingOutlined />
        ),
      });
    }

    return { items: menuItems };
  };

  render() {
    let {
      isSnapToRoadToggled,
      isPIDToggled,
      isEmissionToggled,
      isDownloadSensorDataCsv,
      isDownloadSensorDataCsvWithPitstopMeta,
      isDownloadAllSensorDataCsv,
      isDownloadTripsCsv,
      isDownloadTripAttachments,
      isAllTripsOnMap,
    } = this.state;

    let { car, trips, selectedTrip } = this.props;

    if (trips && !trips.loaded)
      return <Spin style={{ width: '100%' }} tip="Loading..." size="large" />;

    if ((trips && _.isEmpty(trips.data)) || !selectedTrip) return <Empty />;

    return (
      <>
        <Row
          style={{
            height: this.history.location.pathname.includes('/sharetrip')
              ? 65
              : 95,
            marginBottom: this.history.location.pathname.includes('/sharetrip')
              ? 30
              : 0,
            padding: 0,
            width: '100%',
          }}
        >
          <Col xxl={10} sm={24} style={{ padding: 0 }}>
            <Row>
              {customFleets.isMultipleTrips.includes(this.shopId) && (
                <Col span={6} style={{ padding: '8px 0px' }}>
                  <Row>
                    <Col xxl={24} sm={6}>
                      <Switch
                        defaultChecked={isAllTripsOnMap}
                        onChange={() =>
                          this.setState(
                            { isAllTripsOnMap: !isAllTripsOnMap },
                            () =>
                              this.props.setIsAllTripsOnMap(
                                this.state.isAllTripsOnMap
                              )
                          )
                        }
                      />
                    </Col>
                    <Col xxl={24} sm={18}>
                      All Trips On Map
                    </Col>
                  </Row>
                </Col>
              )}

              {!customFleets.gmrv.includes(this.shopId) &&
                !customFleets.npl.includes(this.shopId) && (
                  <Col span={6} style={{ padding: '8px 0px' }}>
                    <Row>
                      <Col xxl={24} sm={6}>
                        <Switch
                          disabled={isAllTripsOnMap}
                          defaultChecked={isSnapToRoadToggled}
                          onChange={() =>
                            this.setState({
                              isSnapToRoadToggled: !isSnapToRoadToggled,
                            })
                          }
                        />
                      </Col>
                      <Col xxl={24} sm={18}>
                        Smooth GPS Plot
                      </Col>
                    </Row>
                  </Col>
                )}

              <Col span={6} style={{ padding: '8px 0px' }}>
                <Row>
                  <Col xxl={24} sm={6}>
                    <Switch
                      disabled={isAllTripsOnMap}
                      checked={isPIDToggled}
                      onChange={() =>
                        this.setState({ isPIDToggled: !isPIDToggled })
                      }
                    />
                  </Col>
                  <Col xxl={24} sm={18}>
                    PID Data
                  </Col>
                </Row>
              </Col>

              {reportConfig.enabledEmissionTableFleets.includes(
                this.shopId
              ) && (
                <Col span={6} style={{ padding: '8px 0px' }}>
                  <Row>
                    <Col xxl={24} sm={6}>
                      <Switch
                        disabled={isAllTripsOnMap}
                        defaultChecked={isEmissionToggled}
                        onChange={() =>
                          this.setState({
                            isEmissionToggled: !isEmissionToggled,
                          })
                        }
                      />
                    </Col>
                    <Col xxl={24} sm={18}>
                      Emission Table
                    </Col>
                  </Row>
                </Col>
              )}
            </Row>
          </Col>

          <Col xxl={14} sm={24} style={{ padding: '8px 0px' }}>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <CopyToClipboard
                  text={`${window.location.origin}/car/${this.props.car.id}/sharetrip/${selectedTrip.tripId}`}
                  onCopy={() => message.success('Link copied to clipboard!')}
                >
                  <TripMenubarButton>
                    <ShareAltOutlined />
                    <TripMenubarText>Share</TripMenubarText>
                  </TripMenubarButton>
                </CopyToClipboard>
              </Col>

              <Col span={6}>
                <Dropdown menu={this.menu()}>
                  <TripMenubarButton>
                    {(() => {
                      const isLoading =
                        isDownloadSensorDataCsv ||
                        isDownloadSensorDataCsvWithPitstopMeta ||
                        isDownloadAllSensorDataCsv ||
                        isDownloadTripsCsv ||
                        isDownloadTripAttachments;
                      if (isLoading) return <LoadingOutlined />;
                      return (
                        <>
                          <DownloadOutlined />
                          <TripMenubarText>Downloads</TripMenubarText>
                          <DownOutlined />
                        </>
                      );
                    })()}
                  </TripMenubarButton>
                </Dropdown>
              </Col>

              {!this.history.location.pathname.includes('/sharetrip') && (
                <Col span={6}>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'similar',
                          label: (
                            <TripMenubarText>
                              View Similar Trips
                            </TripMenubarText>
                          ),
                          onClick: () => this.props.openSimilarTrips(),
                        },
                        {
                          key: 'manual',
                          label: (
                            <TripMenubarText>
                              Select Trips Manually
                            </TripMenubarText>
                          ),
                          onClick: () => this.props.openCompareTrips(),
                        },
                      ],
                    }}
                  >
                    <TripMenubarButton>
                      <TripMenubarText>Compare Trips</TripMenubarText>
                      <DownOutlined />
                    </TripMenubarButton>
                  </Dropdown>
                </Col>
              )}

              <Col span={6}>
                <TripMenubarButton
                  color="danger"
                  variant="solid"
                  onClick={() => this.showDeleteConfirm(this.deleteTrip)}
                >
                  <TripMenubarText>Delete Trip</TripMenubarText>
                </TripMenubarButton>
              </Col>
            </Row>
          </Col>
        </Row>

        <TripDetailArea
          car={car}
          isAllTripsOnMap={isAllTripsOnMap}
          selectedTrip={selectedTrip}
          showAnalytics={isPIDToggled}
          showEmissionTable={isEmissionToggled}
          snapToRoad={isSnapToRoadToggled}
        />
      </>
    );
  }
}

export default withRouter(observer(Utilities));
